import { ProgramContentType } from "data/BlockComponent";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  CellType,
  ElementType,
  PieChartType,
  SingleSelectionType,
} from "data/CellComponent";
import { arraySum } from "logic/logics_common";
import { useEffect, useRef, useState } from "react";
import * as _ from "lodash";
import { Box, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import { useTranslation } from "react-i18next";

const COLORS = [
  theme.vars.palette.primary.solid,
  theme.vars.palette.neutral[200],
  theme.vars.palette.neutral[300],
  theme.vars.palette.neutral[400],
  theme.vars.palette.neutral[500],
  theme.vars.palette.neutral[700],
];

export default function PieChartCell({
  data,
  setData,
  lineIndex,
  cellIndex,
  blockIndex,
  pieChartId,
}: {
  data: ProgramContentType[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  blockIndex: number;
  pieChartId: string;
}) {
  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });

  function groupByLabel(
    objectArray: {
      label: string;
      labelTranslationKey?: string;
      value: string;
    }[]
  ) {
    return objectArray.reduce(function (acc: any, obj) {
      var key = obj["label"];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj.value);
      return acc;
    }, {});
  }
  function groupByLabelTranslationKey(
    objectArray: {
      label: string;
      labelTranslationKey: string;
      value: string;
    }[]
  ) {
    return objectArray.reduce(function (acc: any, obj) {
      var key = obj["labelTranslationKey"];
      if (key?.length > 0) {
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj.value);
      }
      return acc;
    }, {});
  }

  function getPieChartData(data: ProgramContentType[], pieChartId: string) {
    const cellList: CellType[][][] = data?.map(
      (each) => each.lines
    ) as CellType[][][];

    const tempData: { [key: string]: { [key: string]: string } } = {};

    cellList
      .flat(2)
      .map((each) => each.content)
      .forEach((element) => {
        if ((element as ElementType).pieChartDataId) {
          const pieChartDataIdList = (
            element as ElementType
          ).pieChartDataId?.split("/");

          pieChartDataIdList?.forEach((pieChartDataId) => {
            if (pieChartDataId?.includes(`${pieChartId}`)) {
              const dataType = pieChartDataId.split(`_`)[1];
              const id = pieChartDataId.split(`_`)[2];

              if (dataType && id) {
                if (!tempData[id]) {
                  tempData[id] = {};
                }

                tempData[id][dataType] = element?.value as string;

                const singleSelection = element as SingleSelectionType;
                if (
                  singleSelection.options !== undefined &&
                  singleSelection.selectedIndex !== undefined
                ) {
                  tempData[id]["labelTranslationKey"] =
                    singleSelection.options[singleSelection.selectedIndex]
                      .translationKey || "";
                }

                tempData[id]["id"] = element?.requiredId as string;
              }
            }
          });
        }
      });

    const rawData = Object.values(tempData).filter(
      (element) => !!element.label
    );

    const groupedData = groupByLabel(
      rawData as {
        label: string;
        labelTranslationKey?: string;
        value: string;
        id: string;
      }[]
    );
    const groupedDataWithTranslationKey = groupByLabelTranslationKey(
      rawData as {
        label: string;
        labelTranslationKey: string;
        value: string;
        id: string;
      }[]
    );

    const pieChartData = Object.entries(groupedData)
      .map(([key, value], index) => ({
        id: `${index}`,
        label: key.length > 25 ? `${key.substring(0, 25)}...` : key,
        ...(groupedDataWithTranslationKey && {
          labelTranslationKey: Object.keys(groupedDataWithTranslationKey)[
            index
          ],
          label: t_common(Object.keys(groupedDataWithTranslationKey)[index]),
        }),
        value: arraySum((value as string[]).map((each) => parseInt(each))),
      }))
      .sort((a, b) => {
        if (a.label === "통제 불가능") return -1;
        if (b.label === "통제 불가능") return 1;
        return 0;
      });

    return { pieChartData };
  }

  const { pieChartData } = getPieChartData(data, pieChartId);

  useEffect(() => {
    if (
      data &&
      data.length > 0 &&
      !_.isEqual(
        (data[blockIndex].lines[lineIndex][cellIndex].content as PieChartType)
          ?.data,
        pieChartData
      )
    ) {
      setData((data) => {
        if (data) {
          const data_temp = _.cloneDeep(data);
          const currentContent = data_temp[blockIndex].lines[lineIndex][
            cellIndex
          ].content as PieChartType;
          data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
            ...currentContent,
            data: pieChartData,
          };

          pieChartData.forEach((each) => {
            const indexofLabel = data_temp[blockIndex].lines.findIndex(
              (element) =>
                element[0].content?.id ===
                `${pieChartId}_${each.labelTranslationKey || each.label}`
            );

            if (indexofLabel === -1) {
              data_temp[blockIndex].lines.push([
                {
                  type: "temp",
                  content: {
                    id: `${pieChartId}_${
                      each.labelTranslationKey || each.label
                    }`,
                    value: `${each.value}`,
                  },
                },
              ]);
            } else {
              data_temp[blockIndex].lines.splice(indexofLabel, 1, [
                {
                  type: "temp",
                  content: {
                    id: `${pieChartId}_${
                      each.labelTranslationKey || each.label
                    }`,
                    value: `${each.value}`,
                  },
                },
              ]);
            }
          });
          return data_temp;
        }
        return data;
      });
    }
  }, [data]);

  const containerRef = useRef<HTMLElement>(null);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

  const handleResize = () => {
    const containerWidth = containerRef.current?.offsetWidth || 0;
    setIsSmallScreen(containerWidth < 500);
  };

  useEffect(() => {
    handleResize();
    setTimeout(() => {
      handleResize();
    }, 100);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Box
      id={pieChartId}
      ref={containerRef}
      sx={{
        overflow: "visible",
        width: "100%",
        display: "flex",
        justifyContent: "center", // 전체 컨텐츠 중앙 정렬
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          alignItems: "center",
          justifyContent: "center", // 내부 요소들도 중앙 정렬
          width: "100%", // 전체 너비 사용
          maxWidth: "800px", // 최대 너비 제한
        }}
      >
        <Box
          sx={{
            width: isSmallScreen ? "100%" : "300px", // 파이차트 컨테이너 너비 고정
            display: "flex",
            justifyContent: "center",
          }}
        >
          <PieChart
            colors={COLORS}
            sx={{
              [`& .\${pieArcLabelClasses.root}`]: {
                fill: "white",
                fontWeight: "bold",
              },
              width: "100%", // 컨테이너에 맞추기
            }}
            slotProps={{ legend: { hidden: true } }}
            margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
            series={[{ data: pieChartData }]}
            height={250}
            width={isSmallScreen ? undefined : 300} // 명시적 너비 설정
          />
        </Box>

        {/* 커스텀 Legend */}
        <Stack
          direction="column"
          spacing={1}
          sx={{
            ml: isSmallScreen ? 0 : 4, // 충분한 간격
            mt: isSmallScreen ? 2 : 0,
            textAlign: "left",
          }}
        >
          {pieChartData.map((entry, index) => (
            <Stack
              key={entry.id}
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: COLORS[index % COLORS.length],
                  borderRadius: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography level={"body-md"}>{entry.label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
